import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  Updater,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent } from "react";
import { Placeholder, Spinner } from "react-bootstrap";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useDebounce from "hooks/useDebounce";
import { clientesBreadcrumbItems } from "data/cliente";
import ClienteTable, {
  clienteTableColumns,
} from "components/tables/ClienteTable";
import Button from "components/base/Button";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import FilterTab, { FilterTabItem } from "components/common/FilterTab";
import {
  parseAsInteger,
  parseAsString,
  useQueryState,
  createParser,
} from "nuqs";

// Parser customizado para SortingState
const sortParser = createParser({
  parse: (value: string): SortingState => {
    if (!value) return [];
    const [id, direction] = value.split(",");
    return [{ id, desc: direction === "desc" }];
  },
  serialize: (value: SortingState): string => {
    if (!value || value.length === 0) return "";
    return `${value[0].id},${value[0].desc ? "desc" : "asc"}`;
  },
});

const fetchClientes = async ({
  pageIndex,
  pageSize,
  filter,
  sort,
  tabFilter,
}: {
  pageIndex: number;
  pageSize: number;
  filter?: string;
  sort?: SortingState;
  tabFilter?: string;
}) => {
  const { data } = await axios.get("/api/admin/empresa", {
    params: {
      pageSize,
      pageIndex,
      filter,
      sort:
        sort && sort.length > 0
          ? `${sort[0].id},${sort[0].desc ? "desc" : "asc"}`
          : undefined,
      tabFilter,
    },
  });
  return data;
};

const fetchClientesResumo = async () => {
  const { data } = await axios.get("/api/admin/empresa/resumo");
  return data;
};

const Clientes = () => {
  // Estados gerenciados pela URL com nuqs
  const [pageIndex, setPageIndex] = useQueryState(
    "page",
    parseAsInteger.withDefault(0)
  );
  const [pageSize, setPageSize] = useQueryState(
    "pageSize",
    parseAsInteger.withDefault(10)
  );
  const [sort, setSort] = useQueryState(
    "sort",
    sortParser.withDefault([{ id: "ativo", desc: true }])
  );
  const [filter, setFilter] = useQueryState("search", parseAsString);
  const [tabFilter, setTabFilter] = useQueryState("tab", parseAsString);

  const debouncedFilter = useDebounce(filter, 300);

  // Objeto pagination para compatibilidade com TanStack Table
  const pagination: PaginationState = {
    pageIndex,
    pageSize,
  };

  const tabsQuery = useQuery<FilterTabItem[]>({
    queryKey: ["clientesResumo"],
    queryFn: () => fetchClientesResumo(),
    placeholderData: keepPreviousData,
  });

  const tabs =
    tabsQuery.data?.map((t) => {
      return {
        ...t,
        onClick: () => {
          setTabFilter(t.value);
          setPageIndex(0); // Reset para primeira página ao mudar de tab
        },
      };
    }) ?? [];

  const dataQuery = useQuery({
    queryKey: ["clientes", pagination, debouncedFilter, sort, tabFilter],
    queryFn: () =>
      fetchClientes({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        filter: debouncedFilter,
        sort,
        tabFilter,
      }),
    placeholderData: keepPreviousData,
  });

  const table = useReactTable({
    data: dataQuery.data?.itens ?? [],
    columns: clienteTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: (updaterOrValue: Updater<PaginationState>) => {
      if (typeof updaterOrValue === "function") {
        const newPagination = updaterOrValue(pagination);
        setPageIndex(newPagination.pageIndex);
        setPageSize(newPagination.pageSize);
      } else {
        setPageIndex(updaterOrValue.pageIndex);
        setPageSize(updaterOrValue.pageSize);
      }
    },
    onGlobalFilterChange: (value: string | undefined) => {
      setFilter(value ?? null);
      setPageIndex(0); // Reset para primeira página ao pesquisar
    },
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    onSortingChange: (updateOrValue: Updater<SortingState>) => {
      if (typeof updateOrValue === "function") {
        setSort((prev) => {
          const updated = updateOrValue(prev ?? []);
          // Se for o mesmo id do state, inverte o desc
          if (prev && prev[0]?.id === updated[0]?.id) {
            updated[0].desc = !prev[0]?.desc;
          }
          return updated;
        });
      } else {
        setSort(updateOrValue);
      }
      setPageIndex(0); // Reset para primeira página ao ordenar
    },
    pageCount: dataQuery.data?.totalPages ?? 0,
    state: {
      globalFilter: filter ?? "",
      pagination: {
        pageIndex: dataQuery.data?.pageIndex ?? pageIndex,
        pageSize: dataQuery.data?.pageSize ?? pageSize,
      },
      sorting: sort ?? [],
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <div>
      <PageBreadcrumb items={clientesBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">Clientes</h2>
        {tabsQuery.isLoading ? (
          <Placeholder animation="wave" as="div" className="mb-2">
            <Placeholder xs={3} />
          </Placeholder>
        ) : (
          <FilterTab tabItems={tabs} activeValue={tabFilter} className="mb-2" />
        )}

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Pesquisar cliente"
                value={filter ?? ""}
                onChange={handleSearchInputChange}
              />
              <div className="ms-xxl-auto">
                <Button variant="primary" as={Link} to="/revenda/clientes/novo">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Cadastrar
                </Button>
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            <ClienteTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Clientes;
